import type { Problem } from '~/@entities/Problem'
import type {
  CommonResponseDerivationVo,
  CommonResponseDerivationWorkbookVo,
  DerivationProblemSearchByProblemRequest,
  GetPairAndSimilarByIdData,
  GetPersonalizedRecommendedProblemsByData,
  GetPersonalizedRecommendedProblemsByParams,
  GetProblemByDateRangeParams,
  GetProblemForCasParams,
  GetProblemForConceptChipParams,
  GetProblemForConceptParams,
  GetProblemForOneClickParams,
  GetProblemForWorkbookPageParams,
  GetProblemForWorkbookProblemParams,
  GetProblemForWorksheetProblemParams,
  GetRecommendedProblemsByData,
  GetRecommendedProblemsByParams,
  WeakConceptChipPersonalizedCustomWorksheetCreationRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestConfig, type RequestMeta } from '~/http/client'
import type { WithDefaultedOptionals, WithNullableOptionals } from '~/http/request'

import {
  adaptGroupCommonWeakConceptChipRecommendation,
  adaptPairSimilarProblems,
  adaptPersonalizedWeakConceptChipRecommendation,
  adaptWorkbookDerivation,
  adaptWorksheetDerivation,
} from './adapters'
import type {
  PairSimilarProblems,
  PersonalizedWeakConceptChipRecommendation,
  WeakConceptChipRecommendedProblem,
  WeakConceptChipRecommendedProblemEcho,
  WorkbookDerivation,
  WorksheetDerivation,
} from './schemas'

// 조회 조건은 서버가 @ModelAttribute 로 받는 요청 객체(request)와 목록 파라미터로 나뉜다. 생성 타입은 요청 객체를 한 칸으로
// 그리지만 실제 쿼리는 평탄하게 받으므로 펼친다. 요청 객체의 계산 프로퍼티(includedOutOfCurriculum·practiceTestSearchType·
// parsedPageNumbers)는 스펙에 새어 나온 것이라 보내지 않고, Kotlin 생성자 기본값이 있는 필드는 생략할 수 있다. Kotlin
// nullable 필드는 스펙이 optional 로만 그려 null 을 되살린다.
// 2026-10-13 백엔드 develop(cc5f60e17) derivation/controller/request/*.kt 로 확인.
type _Flatten<
  P extends { request: object },
  Defaulted extends keyof P['request'] = never,
  Computed extends keyof P['request'] = never,
> = Omit<P, 'request'> &
  WithDefaultedOptionals<
    WithNullableOptionals<Omit<P['request'], Computed>>,
    Exclude<Defaulted, Computed>
  >

type _Computed = 'includedOutOfCurriculum' | 'practiceTestSearchType'

export type WorksheetProblemDerivationParams = _Flatten<
  GetProblemForWorksheetProblemParams,
  'maxProblemCount' | 'onlyAutoScorable' | 'excludePrevious' | 'arithmeticOnly' | 'arithmeticLevel',
  _Computed
>
export type CasProblemDerivationParams = _Flatten<
  GetProblemForCasParams,
  'maxProblemCount' | 'onlyAutoScorable' | 'excludePrevious',
  _Computed
>
export type ConceptChipDerivationParams = _Flatten<
  GetProblemForConceptChipParams,
  'excludeScoredProblem' | 'onlyAutoScorable' | 'arithmeticOnly'
>
export type WeakConceptDerivationParams = _Flatten<
  GetProblemForConceptParams,
  'onlyAutoScorable' | 'excludePrevious' | 'wrongWorksheetType',
  'includedOutOfCurriculum'
>
export type DateRangeDerivationParams = _Flatten<
  GetProblemByDateRangeParams,
  'revision' | 'maxProblemCount' | 'onlyAutoScorable' | 'excludePrevious',
  'includedOutOfCurriculum'
>
export type OneClickDerivationParams = _Flatten<
  GetProblemForOneClickParams,
  'onlyAutoScorable' | 'practiceTest' | 'excludePrevious' | 'levelType',
  _Computed
>
export type WorkbookProblemDerivationParams = _Flatten<
  GetProblemForWorkbookProblemParams,
  'problemRequestType' | 'includedOutOfCurriculum'
>
export type WorkbookPageDerivationParams = _Flatten<
  GetProblemForWorkbookPageParams,
  'problemRequestType' | 'includedOutOfCurriculum',
  'parsedPageNumbers'
>
export type PairSimilarProblemsBody = WithNullableOptionals<DerivationProblemSearchByProblemRequest>

type _Config = Pick<RequestConfig, 'meta' | 'signal'>

const _getWorksheetDerivation =
  <Params>(url: string) =>
  async (params: Params, config?: _Config): Promise<WorksheetDerivation> => {
    const body = await httpClient().get<CommonResponseDerivationVo>(url, { params, ...config })
    return adaptWorksheetDerivation(body.data)
  }

const _getWorkbookDerivation =
  <Params>(url: string) =>
  async (params: Params, config?: _Config): Promise<WorkbookDerivation> => {
    const body = await httpClient().get<CommonResponseDerivationWorkbookVo>(url, {
      params,
      ...config,
    })
    return adaptWorkbookDerivation(body.data)
  }

/***************** 학습지 오답학습지 *****************/
/** 학습지에서 틀린 문제·유형으로 */
export const getWorksheetProblemDerivation =
  _getWorksheetDerivation<WorksheetProblemDerivationParams>('/derivation/worksheet/problem')
/** 자동시험에서 틀린 문제·유형으로 */
export const getCasProblemDerivation =
  _getWorksheetDerivation<CasProblemDerivationParams>('/derivation/cas/problem')
/** 학생의 취약 유형칩으로 */
export const getConceptChipDerivation = _getWorksheetDerivation<ConceptChipDerivationParams>(
  '/derivation/weak-concept-chip',
)
/** 단원별 취약 유형으로 */
export const getWeakConceptDerivation = _getWorksheetDerivation<WeakConceptDerivationParams>(
  '/derivation/weak-concept',
)
/** 기간 안에 틀린 문제로 */
export const getDateRangeDerivation =
  _getWorksheetDerivation<DateRangeDerivationParams>('/derivation/date-range')
/** 최근 채점한 학습지·소단원으로 */
export const getOneClickDerivation =
  _getWorksheetDerivation<OneClickDerivationParams>('/derivation/one-click')

/***************** 교재 오답학습지 *****************/
export const getWorkbookProblemDerivation = _getWorkbookDerivation<WorkbookProblemDerivationParams>(
  '/derivation/workbook/problem',
)
export const getWorkbookPageDerivation = _getWorkbookDerivation<WorkbookPageDerivationParams>(
  '/derivation/workbook/page',
)

/***************** 쌍둥이·유사 문제 *****************/
/** 문제 하나의 쌍둥이·유사 후보. 응답은 CommonResponse 봉투 없이 본문 그대로다 */
export const getPairSimilarProblems = async (
  problemId: Problem['id'],
  body: PairSimilarProblemsBody,
  meta?: RequestMeta,
): Promise<PairSimilarProblems> => {
  const result = await httpClient().post<GetPairAndSimilarByIdData>(
    `/derivation/problem/${problemId}`,
    body,
    { meta },
  )
  return adaptPairSimilarProblems(result)
}

/***************** 취약 유형칩 (유형분석) *****************/
// 서버는 studentGroupKey 로 나머지 대상 필드를 고르지만 스펙은 셋을 모두 선택으로 그린다. 학년·반 중 하나로 좁힌다.
type _WeakConceptChipQuery = GetPersonalizedRecommendedProblemsByParams
type _GroupTargetKeys = 'studentGroupKey' | 'studentSchoolType' | 'studentGrade' | 'lessonClassId'

/** 추천을 받을 학생 묶음 */
export type WeakConceptChipGroupTarget =
  | {
      studentGroupKey: 'GRADE'
      studentSchoolType: NonNullable<_WeakConceptChipQuery['studentSchoolType']>
      studentGrade: NonNullable<_WeakConceptChipQuery['studentGrade']>
    }
  | {
      studentGroupKey: 'LESSON_CLASS'
      lessonClassId: NonNullable<_WeakConceptChipQuery['lessonClassId']>
    }

export type PersonalizedWeakConceptChipParams = Omit<_WeakConceptChipQuery, _GroupTargetKeys> &
  WeakConceptChipGroupTarget

// 그룹 공통형은 전체 문제 수(maxProblemCount)·유형칩별 문제 수(limitProblemCountPerConceptChip) 중 하나로 정한다.
// 쿼리 직렬화가 null 필드를 빼므로 null 은 '보내지 않음' 이다.
export type WeakConceptChipProblemCount =
  | {
      maxProblemCount: NonNullable<GetRecommendedProblemsByParams['maxProblemCount']>
      limitProblemCountPerConceptChip: null
    }
  | {
      maxProblemCount: null
      limitProblemCountPerConceptChip: NonNullable<
        GetRecommendedProblemsByParams['limitProblemCountPerConceptChip']
      >
    }

export type GroupCommonWeakConceptChipParams = Omit<
  GetRecommendedProblemsByParams,
  _GroupTargetKeys | 'maxProblemCount' | 'limitProblemCountPerConceptChip'
> &
  WeakConceptChipGroupTarget &
  WeakConceptChipProblemCount

/** 학생마다 취약 유형칩에서 고른 추천 문제. 화면은 비었는지만 보고 생성 요청에 그대로 넘긴다 */
export const getPersonalizedWeakConceptChipRecommendation = async (
  params: PersonalizedWeakConceptChipParams,
  meta?: RequestMeta,
): Promise<PersonalizedWeakConceptChipRecommendation> =>
  adaptPersonalizedWeakConceptChipRecommendation(
    await httpClient().get<GetPersonalizedRecommendedProblemsByData>(
      '/derivation/weak-concept-chip/personalized/by-group',
      { params, meta },
    ),
  )

/** 묶음 전체에 공통으로 낼 추천 문제 */
export const getGroupCommonWeakConceptChipRecommendation = async (
  params: GroupCommonWeakConceptChipParams,
  meta?: RequestMeta,
): Promise<WeakConceptChipRecommendedProblem[]> =>
  adaptGroupCommonWeakConceptChipRecommendation(
    await httpClient().get<GetRecommendedProblemsByData>(
      '/derivation/weak-concept-chip/group-common/by-group',
      { params, meta },
    ),
  )

// 서버 요청 클래스는 arithmeticOnly·shareScope 에 기본값을 두고, 스펙의 filterId 는 받지 않는다(생성자에 없다).
// 추천 문제 묶음은 개인 맞춤형 추천 응답을 그대로 싣는다.
export type PersonalizedWeakConceptChipWorksheetBody = WithDefaultedOptionals<
  Omit<
    WeakConceptChipPersonalizedCustomWorksheetCreationRequest,
    'filterId' | 'recommendedProblemsPerStudent'
  >,
  'arithmeticOnly' | 'shareScope'
> & {
  recommendedProblemsPerStudent: {
    studentId: PersonalizedWeakConceptChipRecommendation['recommendedProblemsPerStudent'][number]['studentId']
    problemsWithTagTop: WeakConceptChipRecommendedProblemEcho[]
  }[]
}

/** 학생마다 자기 추천 문제로 맞춤 학습지를 만든다. 응답 본문은 쓰지 않는다 */
export const createPersonalizedWeakConceptChipWorksheets = async (
  body: PersonalizedWeakConceptChipWorksheetBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>('/worksheet/weak-concept-chip/personalized', body, { meta })
}
